import { Chart, ChartTitle, ChartLegend, ChartLegendItem, ChartSeries, ChartSeriesItem, ChartArea, SeriesClickEvent } from "@progress/kendo-react-charts"
import axios from "axios";
import { useEffect, useState } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { Group, Text } from "@progress/kendo-drawing";
import { Grid, GridColumn, GridNoRecords, GridToolbar } from "@progress/kendo-react-grid";
import { AccessToken } from "../config/AccessToken";
import 'hammerjs';
import { Checkbox } from "@progress/kendo-react-inputs";
import { loadingPanel } from "../components/LoadingPanel";

type ZoneWakeup = {
  zoneId: string | number | null;
  deviceMac: string;
  deviceName: string;
};

type ZoneInfo = {
  id: string | number;
  name: string;
  color?: string;
  wakeupList: ZoneWakeup[];
};

type ZoneChartItem = {
  category: string;
  color?: string;
  value: number;
  exploded?: boolean;
};

type AreaDevice = {
  deviceMac: string;
  deviceName: string;
};

type AreaSelectionEvent = {
  target?: { value?: string };
  value?: string;
};

type ZoneWakeupListResponse = {
  data: ZoneInfo[];
};

type ZoneAggregation = {
  allZones: ZoneChartItem[];
  zonesWithDevices: ZoneChartItem[];
  names: string[];
  namesWithDevices: string[];
  countNoZone: number;
  countWithZone: number;
};

/**
 * Component Areas renders a pie chart with the number of areas and the number of areas with devices.
 * When the user clicks on a pie chart slice, the list of devices for that zone is displayed in a grid.
 * The user can select an zone from a dropdown list and the list of devices for that zone is displayed in a grid.
 * The user can hide areas with no devices from the pie chart by clicking on a checkbox.
 * @returns {JSX.Element}
 */
export const Areas = () => {

  const localizationService = useLocalization();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const DEFAULT_LANGUAGE = 'en-US';
  const NO_ZONE_CONFIGURED_MESSAGE = localizationService.toLanguageString('zone.no_value', 'NOT_FOUND');
  const AREA_ACCENT_STRONG = '#075e72';
  const AREA_SURFACE = '#eef8fc';
  const AREA_TITLE_BACKGROUND = '#dbeef5';
  const AREA_TITLE_FONT = "700 12px 'Segoe UI', sans-serif";

  const [zonesInfo, setZonesInfo] = useState<ZoneInfo[]>([])
  const [count, setCount] = useState(0)
  const [countNoZone, setCountNoZone] = useState(0)
  const [countWithZone, setCountWithZone] = useState(0)
  const [zoneName, setZoneName] = useState<string[]>([])

  const [zone, setZone] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  const [devicesInArea, setDevicesInArea] = useState<AreaDevice[]>([]);


  const [instance, accessTokenRequest] = AccessToken();

  const [listIdNamesZone, setListIdNamesZone] = useState<ZoneChartItem[]>([])
  const [listIdNamesZoneWithDevices, setListIdNamesZoneWithDevices] = useState<ZoneChartItem[]>([])
  const [listZoneWithDevices, setListZoneWithDevices] = useState<string[]>([])

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getZoneList()
  }, [])

  const getAreaNameFromDropdown = (event: AreaSelectionEvent): string => {
    return event?.target?.value ?? event?.value ?? '';
  }

  const getAreaNameFromChart = (event: SeriesClickEvent): string => {
    const category = event?.category;
    if (typeof category === 'string') {
      return category;
    }
    return category != null ? String(category) : '';
  }

  const clearAreaSelection = () => {
    setZone('')
    setDevicesInArea([])
    setShowGrid(false)
  }

  const getRequestHeaders = (accessToken: string) => {
    const languageId = localStorage.getItem('languageId') ?? DEFAULT_LANGUAGE;
    return { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
  }

  const resetZoneState = () => {
    setZonesInfo([])
    setCount(0)
    setCountNoZone(0)
    setCountWithZone(0)
    setZoneName([])
    setListIdNamesZone([])
    setListIdNamesZoneWithDevices([])
    setListZoneWithDevices([])
    clearAreaSelection()
  }

  const aggregateZones = (data: ZoneInfo[]): ZoneAggregation => {
    const allZones: ZoneChartItem[] = []
    const zonesWithDevices: ZoneChartItem[] = []
    const names: string[] = []
    const namesWithDevices: string[] = []
    let nextCountNoZone = 0
    let nextCountWithZone = 0

    data.forEach((zone: ZoneInfo) => {
      const value = zone?.wakeupList?.length ?? 0
      const zoneItem = { category: zone.name, color: zone.color, value }

      names.push(zone.name)
      allZones.push(zoneItem)

      if (value === 0) {
        nextCountNoZone++
      } else {
        nextCountWithZone++
        namesWithDevices.push(zone.name)
        zonesWithDevices.push(zoneItem)
      }
    })

    allZones.sort((a, b) => a.value - b.value)
    zonesWithDevices.sort((a, b) => a.value - b.value)

    return {
      allZones,
      zonesWithDevices,
      names,
      namesWithDevices,
      countNoZone: nextCountNoZone,
      countWithZone: nextCountWithZone
    }
  }

  const resolveAccessToken = async (): Promise<string | null> => {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      return storedToken;
    }

    try {
      await instance.initialize();
      const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
      return accessTokenResponse?.accessToken ?? null;
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(accessTokenRequest);
      }
      console.log(error);
      return null;
    }
  };

  const updateSelectionByAreaName = (nameArea: string) => {
    if (!nameArea) return;

    setZone(nameArea);

    const listDevices: AreaDevice[] = [];
    zonesInfo.forEach((zone: ZoneInfo) => {
      if (zone.name === nameArea) {
        zone?.wakeupList.forEach((tag: ZoneWakeup) => {
          if (zone.id === tag.zoneId) {
            listDevices.push({ deviceMac: tag.deviceMac, deviceName: tag.deviceName });
          }
        });
      }
    });

    setDevicesInArea(listDevices);
    setShowGrid(true);

    if (!onToggleZoneDevices) {
      const newData = listIdNamesZone.map((item: ZoneChartItem) => ({
        ...item,
        exploded: item.category === nameArea ? !item.exploded : false
      }));
      setListIdNamesZone(newData);
      return;
    }

    const newData = listIdNamesZoneWithDevices.map((item: ZoneChartItem) => ({
      ...item,
      exploded: item.category === nameArea ? !item.exploded : false
    }));
    setListIdNamesZoneWithDevices(newData);
  }

  const getZoneList = async () => {
    setLoading(true)
    try {
      if (!SERVER_URL) {
        resetZoneState()
        return;
      }

      const accessToken = await resolveAccessToken();
      if (!accessToken) {
        return;
      }

      const headers = getRequestHeaders(accessToken);

      const res = await axios.get<ZoneWakeupListResponse>(`${SERVER_URL}/zone/wakeup/list`, { headers });
      const data: ZoneInfo[] = Array.isArray(res?.data?.data) ? res.data.data : [];

      if (data.length === 0) {
        resetZoneState()
        return;
      }

      const aggregation = aggregateZones(data)

      setCount(data.length)
      setZonesInfo(data)
      setCountNoZone(aggregation.countNoZone)
      setCountWithZone(aggregation.countWithZone)
      setZoneName(aggregation.names)
      setListZoneWithDevices(aggregation.namesWithDevices)
      setListIdNamesZone(aggregation.allZones)
      setListIdNamesZoneWithDevices(aggregation.zonesWithDevices)
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(accessTokenRequest);
      }
      console.log(error);
    } finally {
      setLoading(false)
    }
  }

  const handleChangeArea = (event: AreaSelectionEvent) => {
    const nameArea = getAreaNameFromDropdown(event);
    const enabledZones = onToggleZoneDevices ? listZoneWithDevices : zoneName;
    if (!enabledZones.includes(nameArea) || disabledZoneNames.includes(nameArea)) {
      return;
    }
    updateSelectionByAreaName(nameArea);
  };

  const onClickChart = (event: SeriesClickEvent) => {
    const nameArea = getAreaNameFromChart(event);
    const enabledZones = onToggleZoneDevices ? listZoneWithDevices : zoneName;
    if (!enabledZones.includes(nameArea) || disabledZoneNames.includes(nameArea)) {
      return;
    }
    updateSelectionByAreaName(nameArea);
  }

  const [onToggleZoneDevices, setOnToggleZoneDevices] = useState(true);
  const [disabledZoneNames, setDisabledZoneNames] = useState<string[]>([]);

  const hasAreasData = listIdNamesZone.length > 0;
  const zoneOptions = (onToggleZoneDevices ? listZoneWithDevices : zoneName).filter(
    (name: string) => !disabledZoneNames.includes(name)
  );
  const activeChartData = onToggleZoneDevices ? listIdNamesZoneWithDevices : listIdNamesZone;
  const chartDataWithDisabledState = activeChartData.map((item: ZoneChartItem) => {
    if (!disabledZoneNames.includes(item.category)) {
      return item;
    }

    return {
      ...item,
      value: 0,
      exploded: false
    };
  });
  const chartData = hasAreasData
    ? chartDataWithDisabledState
    : [{ category: NO_ZONE_CONFIGURED_MESSAGE, value: 1, color: '#c6dce6', exploded: false }];

  const onToggleZoneDevicesClick = () => {
    if (!hasAreasData) {
      return;
    }

    setOnToggleZoneDevices((prev: boolean) => !prev);
    setDisabledZoneNames([]);
    clearAreaSelection();
    setListIdNamesZone((prev: ZoneChartItem[]) => prev.map((item) => ({ ...item, exploded: false })));
    setListIdNamesZoneWithDevices((prev: ZoneChartItem[]) => prev.map((item) => ({ ...item, exploded: false })));
  }

  const getChartCategoryFromLegendEvent = (event: any): string => {
    const optionsText = event?.options?.text;
    if (typeof optionsText === 'string' && optionsText.length > 0) {
      return optionsText;
    }

    const pointIndex = typeof event?.pointIndex === 'number' ? event.pointIndex : -1;
    if (pointIndex >= 0 && pointIndex < activeChartData.length) {
      return activeChartData[pointIndex].category;
    }

    if (typeof event?.text === 'string') {
      return event.text;
    }

    return '';
  };

  const applyGrayToLegendText = (element: any) => {
    if (!element) {
      return;
    }

    const hasTextContent = typeof element.content === 'function' && typeof element.content() === 'string';
    if (hasTextContent && typeof element.fill === 'function') {
      element.fill('#9aa4aa');
    }

    if (Array.isArray(element.children)) {
      element.children.forEach((child: any) => applyGrayToLegendText(child));
    }
  };

  const legendItemVisual = (event: any) => {
    const visual = event.createVisual();
    const category = getChartCategoryFromLegendEvent(event);
    if (!category || !disabledZoneNames.includes(category)) {
      return visual;
    }

    applyGrayToLegendText(visual as Group);

    return visual;
  };

  const onLegendItemClick = (event: any) => {
    if (!hasAreasData) {
      if (typeof event?.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }

    const category = getChartCategoryFromLegendEvent(event);
    if (!category) {
      return;
    }

    setDisabledZoneNames((prev: string[]) => {
      if (prev.includes(category)) {
        return prev.filter((name: string) => name !== category);
      }
      return [...prev, category];
    });

    if (zone === category) {
      clearAreaSelection();
    }

    if (typeof event?.preventDefault === 'function') {
      event.preventDefault();
    }
  };

  const chartTitleText = hasAreasData
    ? `${localizationService.toLanguageString('zone.number_of_zones', 'NOT_FOUND')}: ${count} • ${localizationService.toLanguageString('zone.zones_with_devices', 'NOT_FOUND')}: ${countWithZone} • ${localizationService.toLanguageString('zone.zones_with_no_devices', 'NOT_FOUND')}: ${countNoZone}`
    : NO_ZONE_CONFIGURED_MESSAGE;

  const chartCenterLabel = hasAreasData
    ? localizationService.toLanguageString('zone.number_of_zones', 'NOT_FOUND')
    : NO_ZONE_CONFIGURED_MESSAGE;

  const chartCenterCount = hasAreasData ? count : 0;

  const donutCenterRenderer = () => (
    <div
      className="areas-chart-center-stat"
    >
      <div className="areas-chart-center-count">{chartCenterCount}</div>
      {/*<div className="areas-chart-center-label">{chartCenterLabel}</div>*/}
    </div>
  );

  const gridToolbarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '10px 10px 0 0',
    background: 'linear-gradient(180deg, #d8edf1 0%, #cfe8ed 100%)',
    borderBottom: '1px solid #9ec8cf',
    color: '#0b4a53'
  };

  const gridToolbarTitleStyle = {
    margin: 0,
    color: '#0b4a53',
    fontSize: 'var(--fs-card-title)',
    fontWeight: 700,
    letterSpacing: 0
  };

  const gridToolbarBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '34px',
    height: '28px',
    padding: '0 10px',
    borderRadius: '999px',
    background: '#ecf7f9',
    border: '1px solid #99c4cb',
    color: '#0f6671',
    fontWeight: 700,
    fontSize: 'var(--fs-body)'
  };

  const areasGridMotionCss = `
    .areas-grid-surface {
      border: 1px solid #bdd8dd;
      border-radius: 12px;
      overflow: hidden;
      background: linear-gradient(180deg, #f5fbfc 0%, #eaf5f7 100%);
      box-shadow: 0 10px 24px rgba(9, 64, 72, 0.14);
    }

    .areas-grid-colorized .k-grid-toolbar {
      border-left: none !important;
      border-right: none !important;
      margin: 0;
    }

    .areas-grid-colorized .k-grid-header th {
      background: linear-gradient(180deg, #d4eaf0 0%, #c7e1e8 100%);
      color: #0d4a54;
      border-bottom: 1px solid #9ec5cc;
      border-right: 1px solid #b7d6dc;
      padding: 0.6rem;
    }

    .areas-grid-colorized .k-table-tbody > tr > td {
      background: linear-gradient(180deg, rgba(250, 254, 255, 0.98) 0%, rgba(240, 250, 252, 0.98) 100%);
      border-bottom: 1px solid #c5dee3;
      border-right: 1px solid #d6e9ed;
      color: #114f58;
      padding: 0.56rem 0.62rem;
      vertical-align: middle;
    }

    .areas-grid-colorized .k-table-tbody > tr:nth-child(even) > td {
      background: linear-gradient(180deg, rgba(243, 251, 253, 0.98) 0%, rgba(232, 245, 248, 0.98) 100%);
    }

    .areas-grid-colorized .k-table-tbody > tr:hover > td {
      background: linear-gradient(180deg, #e4f4f7 0%, #d7edf1 100%) !important;
      box-shadow: inset 0 0 0 1px rgba(10, 85, 97, 0.14);
    }

    .areas-grid-colorized .k-grid-norecords td {
      padding: 0 !important;
      border-bottom: 1px solid #c5dee3;
    }

    .areas-grid-colorized .areas-grid-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 82px;
      font-weight: 700;
      letter-spacing: 0.01em;
      color: #0f6671;
      background: linear-gradient(180deg, rgba(246, 252, 253, 0.95) 0%, rgba(235, 246, 248, 0.95) 100%);
    }
  `;

  return (
    <>
      {loading && loadingPanel}
      <style>{areasGridMotionCss}</style>
      <div className={`flex-parent-element areas-layout-root areas-motion-root ${showGrid ? 'areas-grid-open' : ''}`}>
          <div className="flex-left-child-element areas-chart-pane areas-motion-card areas-motion-card-primary areas-chart-column">
            <Chart
              donutCenterRender={donutCenterRenderer}
              style={{ height: '100%' }}
              className="areas-chart"
              onSeriesClick={(e: SeriesClickEvent) => onClickChart(e)}
              onLegendItemClick={onLegendItemClick}
            >
              <ChartTitle
                text={chartTitleText}
                color={AREA_ACCENT_STRONG}
                background={AREA_TITLE_BACKGROUND}
                font={AREA_TITLE_FONT}
                margin={{ bottom: 24 }}
              />
              <ChartLegend
                position="bottom"
                reverse={false}
                labels={{ color: AREA_ACCENT_STRONG }}
              >
                <ChartLegendItem visual={legendItemVisual} />
              </ChartLegend>
              <ChartArea background={AREA_SURFACE} margin={10} />
              <ChartSeries>
                <ChartSeriesItem
                  type="donut"
                  //overlay={{ gradient: "sharpBevel" }}
                  data={chartData}
                  field="value"
                  categoryField="category"
                  explodeField="exploded"
                  colorField="color"
                  tooltip={{ visible: hasAreasData }}
                //connectors={{ width: 0 }}
                //line={{ color: "red" }}
                >
                  {/*<ChartSeriesLabels
                    color="#018CA5"
                    background="none"
                    content={labelContent}
                    font="bold"
                  //position="outsideEnd"
                  //align="circle"
                  />*/}
                </ChartSeriesItem>
              </ChartSeries>
            </Chart>
          </div>

          <div className="flex-right-child-element-areas areas-control-pane areas-motion-card areas-motion-card-secondary areas-control-column">
            <div className="areas-control-header">
              <h2 className="areas-control-title">{localizationService.toLanguageString('zone.zones_list', 'NOT_FOUND')}</h2>
            </div>
            <div className="areas-panel-content">
              <DropDownList
                data={zoneOptions}
                value={zone.length > 0 ? zone : null}
                defaultItem={localizationService.toLanguageString('zone.select_zone', 'NOT_FOUND')}
                onChange={handleChangeArea}
                className="areas-select"
                title={localizationService.toLanguageString('zone.select_zone', 'NOT_FOUND')}
                disabled={!hasAreasData}
              />
              {showGrid &&
                <>
                  <h4 className="areas-grid-title areas-motion-inline">{localizationService.toLanguageString('zone.device_list', 'NOT_FOUND')}</h4>
                  <div className="areas-grid areas-motion-inline areas-grid-surface" style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto' }}>
                    <Grid
                      className="areas-grid-colorized"
                      data={devicesInArea}
                      dataItemKey="deviceMac"
                      sortable={false}
                      filterable={false}
                      pageable={false}
                      scrollable={'none'}
                    >
                      <GridNoRecords>
                        <div className="areas-grid-empty">{localizationService.toLanguageString('zone.no_value', 'NOT_FOUND')}</div>
                      </GridNoRecords>
                      <GridToolbar style={gridToolbarStyle}>
                        <h5 style={gridToolbarTitleStyle}>{zone || localizationService.toLanguageString('zone.select_zone', 'NOT_FOUND')}</h5>
                        <span style={gridToolbarBadgeStyle}>{devicesInArea.length}</span>
                      </GridToolbar>
                      <GridColumn field="deviceMac" title="Mac" />
                      <GridColumn field="deviceName" title={localizationService.toLanguageString('zone.name', 'NOT_FOUND')} />
                    </Grid>
                  </div>
                </>
              }
            </div>
            <div className="areas-toggle-row">
              <div className="areas-toggle-label-wrap">
                <Checkbox
                  label={localizationService.toLanguageString('zone.hide_zone_no_devices', 'NOT_FOUND')}
                  value={onToggleZoneDevices}
                  onChange={onToggleZoneDevicesClick}
                  disabled={!hasAreasData}
                />
              </div>
            </div>
          </div>
      </div>
    </>
  )
}